import React, { useRef } from "react";

interface Props {
  pendingFile: File | null;
  uploading: boolean;
  description?: string;
  onFileSelect: (file: File) => void;
  onConfirmUpload: () => void;
  onCancelFile: () => void;
  onDownloadSample: () => void;
  sampleLabel?: string;
}

export default function CsvDropZone({
  pendingFile,
  uploading,
  description = "Max 500 rows",
  onFileSelect,
  onConfirmUpload,
  onCancelFile,
  onDownloadSample,
  sampleLabel = "Download Sample CSV",
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) onFileSelect(file);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.style.borderColor = "#dee2e6";
    e.currentTarget.style.background = "#f9f9f9";
    const file = e.dataTransfer.files[0];
    if (file) onFileSelect(file);
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        style={{ display: "none" }}
        onChange={handleFileInput}
      />

      <div
        className="rounded text-center"
        style={{
          border: "2px dashed #dee2e6",
          padding: "36px 20px",
          background: "#f9f9f9",
          transition: "border-color .15s, background .15s",
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.currentTarget.style.borderColor = "#6D9300";
          e.currentTarget.style.background = "#f4f8e8";
        }}
        onDragLeave={(e) => {
          e.currentTarget.style.borderColor = "#dee2e6";
          e.currentTarget.style.background = "#f9f9f9";
        }}
        onDrop={handleDrop}
      >
        {uploading ? (
          <div className="d-flex flex-column align-items-center gap-2 py-2">
            <div className="spinner-border" role="status" style={{ color: "#6D9300" }} />
            <span className="fw-medium text-muted" style={{ fontSize: "0.9rem" }}>Uploading…</span>
          </div>
        ) : pendingFile ? (
          <div className="d-flex flex-column align-items-center gap-3 py-2">
            <div style={{
              width: 48, height: 48, borderRadius: 10,
              background: "#f4f9e6", border: "2px solid #6D9300",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" fill="none" stroke="#6D9300" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
            </div>
            <div className="text-center">
              <p className="mb-0 fw-semibold" style={{ fontSize: "0.9rem", color: "#111827" }}>{pendingFile.name}</p>
              <p className="mb-0 text-muted" style={{ fontSize: "0.78rem" }}>
                {(pendingFile.size / 1024).toFixed(1)} KB &nbsp;·&nbsp; Ready to upload
              </p>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm d-inline-flex align-items-center gap-2 px-4"
                style={{ backgroundColor: "#6D9300", color: "white", borderColor: "#6D9300" }}
                onClick={onConfirmUpload}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                  <polyline points="16 16 12 12 8 16" />
                  <line x1="12" y1="12" x2="12" y2="21" />
                  <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                </svg>
                Upload CSV
              </button>
              <button className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1" onClick={onCancelFile}>
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                </svg>
                Remove
              </button>
            </div>
          </div>
        ) : (
          <>
            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="none" stroke="#b5b5c3" strokeWidth="1.5" viewBox="0 0 24 24" className="mb-3">
              <polyline points="16 16 12 12 8 16" />
              <line x1="12" y1="12" x2="12" y2="21" />
              <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
            </svg>
            <p className="mb-1 fw-semibold" style={{ fontSize: "0.9rem" }}>Drag &amp; drop your CSV file here</p>
            <p className="mb-3 text-muted" style={{ fontSize: "0.8rem" }}>{description}</p>
            <button
              className="btn d-inline-flex align-items-center gap-2 px-4"
              style={{ backgroundColor: "#6D9300", color: "white", borderColor: "#6D9300" }}
              onClick={() => fileInputRef.current?.click()}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                <polyline points="16 16 12 12 8 16" />
                <line x1="12" y1="12" x2="12" y2="21" />
                <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
              </svg>
              Import CSV
            </button>
          </>
        )}
      </div>

      <div className="mt-3 d-flex align-items-center gap-3">
        <button
          className="btn btn-sm d-inline-flex align-items-center gap-2"
          style={{ color: "#6D9300", borderColor: "#6D9300", backgroundColor: "transparent" }}
          onClick={onDownloadSample}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          {sampleLabel}
        </button>
        <small className="text-muted">Use this template to prepare your upload file</small>
      </div>
    </>
  );
}
